import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Tag, Space, Typography, Card, Skeleton, Badge, Popover, Descriptions, Empty, Alert, Button, Modal, message } from "antd";
import { PlusOutlined, DeleteOutlined, ImportOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";
import { formatDistance } from "date-fns";
import { it } from "date-fns/locale";
import useOrgDetails from "../hooks/useOrgDetails";
import StripeLogo from "../../shared/images/stripe.png";
import AddModulesModal from "./AddModulesModal";
import ImportQuoteModal from "./ImportQuoteModal";
// Removed ServiziUsage from this page; usage now shown in Riepilogo

const { Title } = Typography;

const query = `
  query fetchOrgModules($where: Org_moduleWhereInput) {
    org_modules(where: $where, first: 10000) {
      edges {
        node {
          objectId
          enabled
          enabled_by
          expiry_date
          createdAt
          updatedAt
          module_pointer {
            objectId
            name
            description
            icon {
              url
            }
            module_category_pointer {
              objectId
              name
              color
              darker_color
            }
            price
            is_active
            is_main
            is_product_line
            sku
            una_tantum
            quantity
            included_quantity
            charge_by_quantity
            price_for_quantity
            unit_of_measure
            included_service_relation {
              edges {
                node {
                  objectId
                  name
                }
              }
            }
            related_service_relation {
              edges {
                node {
                  objectId
                  name
                }
              }
            }
            target_relation {
              edges {
                node {
                  objectId
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const OrgAbbonamento = ({ orgId }) => {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deletingModule, setDeletingModule] = useState(false);
  const { org, loading: orgLoading } = useOrgDetails(orgId);
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  // Removed module discounts fetch as usage panel has been moved

  const fetchData = useCallback(async () => {
    try {
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query,
          variables: {
            where: {
              org_pointer: {
                have: {
                  objectId: {
                    equalTo: orgId
                  }
                }
              }
            }
          }
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const modulesData = response.data.org_modules.edges
        .map((edge) => ({
          ...edge.node,
          key: edge.node.objectId
        }))
        .sort((a, b) => (a.module_pointer?.name || "").localeCompare(b.module_pointer?.name || ""));

      setModules(modulesData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching org modules:", error);
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl, orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Removed module discounts fetch effect

  const handleDeleteModule = (module) => {
    setModuleToDelete(module);
    setDeleteModalVisible(true);
  };

  const confirmDeleteModule = async () => {
    if (!moduleToDelete) return;

    setDeletingModule(true);
    try {
      const token = await getTokenSilently();
      const deleteQuery = `
        mutation DeleteOrgModule($id: ID!) {
          deleteOrg_module(input: { id: $id }) {
            org_module {
              objectId
            }
          }
        }
      `
        .replace(/\r?\n|\r/g, "")
        .replace(/\t/g, " ");

      await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: deleteQuery,
          variables: {
            id: moduleToDelete.objectId
          }
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success("Modulo rimosso con successo");
      setDeleteModalVisible(false);
      setModuleToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting module:", error);
      message.error("Errore durante la rimozione del modulo");
    } finally {
      setDeletingModule(false);
    }
  };

  const handleAddModules = () => {
    setAddModalVisible(true);
  };

  const handleImportQuote = () => {
    setImportModalVisible(true);
  };

  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: "12px 48px" }}>
        <Descriptions title="Dettagli Modulo" column={3} size="small" bordered>
          {record.module_pointer?.description && (
            <Descriptions.Item label="Descrizione" span={3}>
              {record.module_pointer.description}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="SKU">{record.module_pointer?.sku || "-"}</Descriptions.Item>
          <Descriptions.Item label="Prezzo">{record.module_pointer?.price ? `${record.module_pointer.price.toFixed(2)} €` : "-"}</Descriptions.Item>
          <Descriptions.Item label="A Quantità">
            <Tag color={record.module_pointer?.quantity ? "purple" : "default"}>{record.module_pointer?.quantity ? "Si" : "No"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Quantità Inclusa">{record.module_pointer?.included_quantity ? `${record.module_pointer.included_quantity}${record.module_pointer.unit_of_measure ? ` ${record.module_pointer.unit_of_measure}` : ""}` : "0"}</Descriptions.Item>
          <Descriptions.Item label="Prezzo per Quantità">{record.module_pointer?.price_for_quantity ? `${record.module_pointer.price_for_quantity.toFixed(2)} €` : "-"}</Descriptions.Item>
          <Descriptions.Item label="Una Tantum">
            <Tag color={record.module_pointer?.una_tantum ? "success" : "default"}>{record.module_pointer?.una_tantum ? "Si" : "No"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Scadenza">{record.expiry_date ? new Date(record.expiry_date).toLocaleDateString() : "-"}</Descriptions.Item>
        </Descriptions>

        {(record.module_pointer?.included_service_relation?.edges?.length > 0 || record.module_pointer?.target_relation?.edges?.length > 0 || record.module_pointer?.related_service_relation?.edges?.length > 0) && (
          <>
            <Descriptions title="Relazioni" column={1} size="small" bordered style={{ marginTop: "12px" }}>
              {record.module_pointer?.included_service_relation?.edges?.length > 0 && (
                <Descriptions.Item label="Moduli Inclusi">
                  <Space size={[0, 4]} wrap>
                    {record.module_pointer.included_service_relation.edges.map((edge) => (
                      <Tag key={edge.node.objectId} style={{ margin: "2px" }}>
                        {edge.node.name}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              {record.module_pointer?.target_relation?.edges?.length > 0 && (
                <Descriptions.Item label="Target">
                  <Space size={[0, 4]} wrap>
                    {record.module_pointer.target_relation.edges.map((edge) => (
                      <Tag key={edge.node.objectId} color="cyan" style={{ margin: "2px" }}>
                        {edge.node.name}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              {record.module_pointer?.related_service_relation?.edges?.length > 0 && (
                <Descriptions.Item label="Servizi Correlati">
                  <Space size={[0, 4]} wrap>
                    {record.module_pointer.related_service_relation.edges.map((edge) => (
                      <Tag key={edge.node.objectId} color="blue" style={{ margin: "2px" }}>
                        {edge.node.name}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}

        <Descriptions title="Informazioni di Sistema" column={2} size="small" bordered style={{ marginTop: "12px" }}>
          <Descriptions.Item label="ID Sottoscrizione">{record.objectId}</Descriptions.Item>
          <Descriptions.Item label="Data Attivazione">
            {record.createdAt
              ? formatDistance(new Date(record.createdAt), new Date(), {
                  locale: it,
                  addSuffix: true
                })
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Ultimo Aggiornamento">
            {record.updatedAt
              ? formatDistance(new Date(record.updatedAt), new Date(), {
                  locale: it,
                  addSuffix: true
                })
              : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  };

  const columns = [
    {
      title: "Icon",
      key: "icon",
      render: (_, record) => {
        const iconUrl = record.module_pointer?.icon?.url;
        return iconUrl ? (
          <img
            src={iconUrl}
            alt=""
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentNode.textContent = "-";
            }}
            style={{
              width: 36,
              height: 36,
              objectFit: "contain",
              verticalAlign: "middle"
            }}
          />
        ) : (
          "-"
        );
      }
    },
    {
      title: "Nome Modulo",
      key: "name",
      render: (_, record) => record.module_pointer?.name || "-",
      sorter: (a, b) => (a.module_pointer?.name || "").localeCompare(b.module_pointer?.name || "")
    },
    {
      title: "SKU",
      key: "sku",
      render: (_, record) => {
        const sku = record.module_pointer?.sku;
        return sku ? (
          <Tag color="default" style={{ background: "#fafafa" }}>
            {sku}
          </Tag>
        ) : (
          "-"
        );
      }
    },
    {
      title: "Quantità",
      key: "quantity",
      render: (_, record) => {
        const module = record.module_pointer;
        if (!module?.quantity && !module?.included_quantity) {
          return "-";
        }

        return (
          <Space size={4} wrap>
            {module.quantity && <Tag color="purple">A quantità</Tag>}
            {module.included_quantity > 0 && (
              <Tag color="green">
                Incl. {module.included_quantity}
                {module.unit_of_measure ? ` ${module.unit_of_measure}` : ""}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: "Stato",
      dataIndex: "enabled",
      key: "enabled",
      filters: [
        { text: "Attivo", value: true },
        { text: "Disattivato", value: false }
      ],
      onFilter: (value, record) => record.enabled === value,
      render: (enabled) => <Tag color={enabled ? "success" : "error"}>{enabled ? "Attivo" : "Disattivato"}</Tag>
    },
    {
      title: "Categoria",
      key: "category",
      filters: [...new Set(modules.map((m) => m.module_pointer?.module_category_pointer?.name).filter(Boolean))].map((cat) => ({
        text: cat,
        value: cat
      })),
      onFilter: (value, record) => record.module_pointer?.module_category_pointer?.name === value,
      render: (_, record) => {
        const category = record.module_pointer?.module_category_pointer?.name;
        const color = record.module_pointer?.module_category_pointer?.darker_color;
        return category ? <Tag color={color || "default"}>{category}</Tag> : "-";
      }
    },
    {
      title: "Tipologia",
      key: "is_product_line",
      filters: [
        { text: "Linea di Prodotto", value: true },
        { text: "Modulo", value: false }
      ],
      onFilter: (value, record) => record.module_pointer?.is_product_line === value,
      render: (_, record) => {
        const isProductLine = record.module_pointer?.is_product_line;
        return <Tag color={isProductLine ? "blue" : "green"}>{isProductLine ? "Linea di Prodotto" : "Modulo"}</Tag>;
      }
    },
    {
      title: "Principale",
      key: "is_main",
      filters: [
        { text: "Principale", value: true },
        { text: "Aggiuntivo", value: false }
      ],
      onFilter: (value, record) => record.module_pointer?.is_main === value,
      render: (_, record) => {
        const isMain = record.module_pointer?.is_main;
        const isProductLine = record.module_pointer?.is_product_line;
        return isProductLine ? "-" : <Tag color={isMain ? "purple" : "orange"}>{isMain ? "Principale" : "Aggiuntivo"}</Tag>;
      }
    },
    {
      title: "Abilitato da",
      dataIndex: "enabled_by",
      key: "enabled_by",
      filters: [
        { text: "Stripe", value: "stripe" },
        { text: "Manuale", value: "manual" }
      ],
      onFilter: (value, record) => record.enabled_by === value,
      render: (enabledBy) => {
        if (enabledBy === "stripe") {
          return (
            <img
              src={StripeLogo}
              alt="Stripe"
              style={{
                height: 20,
                objectFit: "contain"
              }}
            />
          );
        }
        return <Tag color="blue">{enabledBy || "N/A"}</Tag>;
      }
    },
    {
      title: "Scadenza",
      dataIndex: "expiry_date",
      key: "expiry_date",
      sorter: (a, b) => {
        if (!a.expiry_date) return 1;
        if (!b.expiry_date) return -1;
        return new Date(a.expiry_date) - new Date(b.expiry_date);
      },
      render: (expiryDate) => {
        if (!expiryDate) return "-";
        const date = new Date(expiryDate);
        const now = new Date();
        const isExpired = date < now;
        const isExpiringSoon = date < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days

        return <Tag color={isExpired ? "error" : isExpiringSoon ? "warning" : "success"}>{date.toLocaleDateString()}</Tag>;
      }
    },
    {
      title: "Relazioni",
      key: "relations",
      render: (_, record) => {
        const includedServices = record.module_pointer?.included_service_relation?.edges || [];
        const targets = record.module_pointer?.target_relation?.edges || [];
        const relatedServices = record.module_pointer?.related_service_relation?.edges || [];

        return (
          <Space size="small">
            {includedServices.length > 0 && (
              <Popover
                content={
                  <div style={{ maxWidth: "400px" }}>
                    {includedServices.map((edge) => (
                      <Tag key={edge.node.objectId} style={{ margin: "2px" }}>
                        {edge.node.name}
                      </Tag>
                    ))}
                  </div>
                }
                title="Moduli Inclusi"
                trigger="hover"
              >
                <Tag style={{ cursor: "pointer" }}>{includedServices.length} inclusi</Tag>
              </Popover>
            )}
            {targets.length > 0 && (
              <Popover
                content={
                  <div style={{ maxWidth: "400px" }}>
                    {targets.map((edge) => (
                      <Tag key={edge.node.objectId} color="cyan" style={{ margin: "2px" }}>
                        {edge.node.name}
                      </Tag>
                    ))}
                  </div>
                }
                title="Targets"
                trigger="hover"
              >
                <Tag color="cyan" style={{ cursor: "pointer" }}>
                  {targets.length} targets
                </Tag>
              </Popover>
            )}
            {relatedServices.length > 0 && (
              <Popover
                content={
                  <div style={{ maxWidth: "400px" }}>
                    {relatedServices.map((edge) => (
                      <Tag key={edge.node.objectId} color="blue" style={{ margin: "2px" }}>
                        {edge.node.name}
                      </Tag>
                    ))}
                  </div>
                }
                title="Servizi Correlati"
                trigger="hover"
              >
                <Tag color="blue" style={{ cursor: "pointer" }}>
                  {relatedServices.length} correlati
                </Tag>
              </Popover>
            )}
          </Space>
        );
      }
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteModule(record);
            }}
          />
        </Space>
      )
    }
  ];

  const existingModuleIds = useMemo(() => modules.map((moduleRecord) => moduleRecord.module_pointer?.objectId).filter(Boolean), [modules]);

  if (loading || orgLoading) {
    return <Skeleton active />;
  }

  return (
    <div style={{ margin: "16px 0" }}>
      {(org?.tipo_istanza || org?.tipo_licenza) && (
        <Alert
          message={
            <Space size="large">
              {org?.tipo_istanza && (
                <Space>
                  <strong>Tipo Istanza:</strong>
                  <Tag color="blue" style={{ fontSize: "14px", padding: "4px 12px" }}>
                    {org.tipo_istanza}
                  </Tag>
                </Space>
              )}
              {org?.tipo_licenza && (
                <Space>
                  <strong>Tipo Licenza:</strong>
                  <Tag color="purple" style={{ fontSize: "14px", padding: "4px 12px" }}>
                    {org.tipo_licenza}
                  </Tag>
                </Space>
              )}
            </Space>
          }
          type="info"
          style={{ marginBottom: "16px" }}
        />
      )}
      {org?.tipo_licenza && org.tipo_licenza !== "module" && modules.length > 0 && <Alert message="Attenzione: Tipo licenza non è 'module' ma sono presenti moduli sottoscritti" description="Questa organizzazione ha moduli attivi nonostante il tipo di licenza non sia impostato su 'module'. Verificare la configurazione." type="warning" showIcon style={{ marginBottom: "16px" }} />}
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px"
          }}
        >
          <Title level={4}>
            <Space>
              Moduli Sottoscritti
              <Badge overflowCount={5000} count={modules.length} />
            </Space>
          </Title>
          <Space>
            <Button icon={<ImportOutlined />} onClick={handleImportQuote}>
              Importa da preventivo
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddModules}>
              Aggiungi
            </Button>
          </Space>
        </div>
        {modules.length === 0 ? (
          <Empty description="Nessun modulo sottoscritto" />
        ) : (
          <Table
            size="small"
            dataSource={modules}
            columns={columns}
            loading={loading}
            rowKey="key"
            pagination={false}
            scroll={{ x: true }}
            expandable={{
              expandedRowRender,
              expandRowByClick: false
            }}
          />
        )}
      </Card>

      <Modal
        title="Conferma rimozione modulo"
        open={deleteModalVisible}
        onOk={confirmDeleteModule}
        onCancel={() => {
          setDeleteModalVisible(false);
          setModuleToDelete(null);
        }}
        confirmLoading={deletingModule}
        okText="Rimuovi"
        cancelText="Annulla"
        okButtonProps={{ danger: true }}
      >
        <p>
          Sei sicuro di voler rimuovere il modulo <strong>{moduleToDelete?.module_pointer?.name}</strong> da questa organizzazione?
        </p>
        <p>Questa azione non può essere annullata.</p>
      </Modal>

      <AddModulesModal
        visible={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onSuccess={() => {
          setAddModalVisible(false);
          fetchData();
        }}
        orgId={orgId}
        existingModuleIds={existingModuleIds}
      />

      <ImportQuoteModal
        visible={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        onSuccess={() => {
          setImportModalVisible(false);
          fetchData();
        }}
        orgId={orgId}
        org={org}
      />
    </div>
  );
};

export default OrgAbbonamento;
